// Phase 5.4 · 간편 주문 뷰 — 8단계 가이드형 처리
// - ① 주문확인 ② 결제확인 ③ 재고확인 ④ 상품준비 ⑤ 출고관리 ⑥ 송장등록 ⑦ 발송 ⑧ 완료
// - 각 단계 클릭 시 해당 주문만 필터링 표시 · 다중선택 · [선택 N건 처리] 로 다음 단계 이동
// - STEP OMS-UI-1 (2026-02) · ⑤ 출고관리 embed:
//     ⑤ 클릭 · 또는 ④ 피킹 시작 성공 후 → 페이지 이탈 없이 본문에서 기존
//     FulfillmentSection 을 그대로 REUSE 렌더 (① ~ ⑧ 트래커/상단 헤더/문제 주문
//     바는 유지).  route `/seller/dashboard?section=fulfillment` 는 유지.
// - '문제 주문' 별도 그룹
// - [상세 관리] → 기존 OMS Workbench 렌더링 (SellerDashboard renders detailed section)
// 기존 API 재사용: /seller/oms/summary, /seller/oms/orders, /seller/orders?status=,
//                 /seller/fulfillment/{sid}/pick, /seller/orders/bulk-status
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, ShoppingBag, ChevronRight, ChevronLeft, AlertTriangle, RotateCcw, PackagePlus, Play, Truck, CheckCircle2, RefreshCw, FileSpreadsheet, Upload, Search, X as XIcon, Package } from 'lucide-react';
import { STAGES, stageCount, fetchOmsCounts } from '@/lib/omsStages';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import api from '@/lib/api';
import BulkInvoiceUploadModal from '../BulkInvoiceUploadModal';
import SellerCombinedShipmentModal from '../SellerCombinedShipmentModal';
import Seller3PLCombinedRequestModal from '../Seller3PLCombinedRequestModal';
import CarrierExportModal from '../CarrierExportModal';
// STEP OMS-UI-1 · ⑤ 출고관리 embed · 별도 route 화면 컴포넌트 그대로 REUSE.
import FulfillmentSection from '../FulfillmentSection';
// STEP OMS-UI-3A · 상황별 카톡 안내 modal (Foundation · No send).
import KakaoContextGuideModal from './KakaoContextGuideModal';
// STEP OMS-PARTIAL-PICK-1 · 다상품 주문 선택 피킹 modal.
import SelectivePickModal from './SelectivePickModal';
import { BankDepositsPanel } from '../BankAutoConfirm';
import CardTransactionsPanel from '../CardTransactionsPanel';

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

// D-6E FINAL CLEANUP §13/§14 · STAGES + stageCount 는 `@/lib/omsStages` 에서
// SINGLE source of truth 로 관리 · SimpleHome.js 와 동일 정의 REUSE.

// 문제 주문 카테고리
const PROBLEM_TABS = [
  { key: 'shortage',  label: '재고부족',   test: (c) => (c.waiting_stock || 0) + (c.has_out_of_stock || 0) },
  { key: 'partial',   label: '부분재고',   test: (c) => c.partial_shippable || 0 },
  { key: 'payment',   label: '결제문제',   test: () => 0 /* placeholder */ },
  { key: 'inbound',   label: '입고대기',   test: () => 0 /* from ops summary */ },
  { key: 'rma',       label: '취소·교환·반품', test: () => 0 /* from ops */ },
];

export default function SimpleOrders({ goTo, onOpenDetailedSection, onOpenUnifiedDetail }) {
  // Read initial stage from URL (?stage=<key>) — allows deep-linking from SimpleHome cards.
  const initialStage = (() => {
    try {
      const s = new URLSearchParams(window.location.search).get('stage');
      if (s && STAGES.some((x) => x.key === s)) return s;
    } catch { /* ignore */ }
    return 'payment_confirm';
  })();
  const [stageKey, setStageKey] = useState(initialStage);
  const [counts, setCounts] = useState({ oms: {}, pay_pending: 0, shipping: 0 });
  // SHIPPING V1 FINAL UI CLOSE · ⑥ 발송처리 확인 모달
  const [shipConfirmOpen, setShipConfirmOpen] = useState(false);
  const [problemCounts, setProblemCounts] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // A4-2A-1 · Common UX (search · date filter · sort · pagination)
  const PAGE_SIZE = 50;
  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [dateChip, setDateChip] = useState('all');     // 'all' | 'today' | '7d' | '30d' | 'custom'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortUi, setSortUi] = useState('recent');       // 'recent' | 'oldest'
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, total_pages: 1 });
  const searchTimerRef = useRef(null);
  const _iso = (d) => d.toISOString().slice(0, 10);
  const _dateRange = () => {
    if (dateChip === 'today') { const t = new Date(); return [_iso(t), _iso(t)]; }
    if (dateChip === '7d')    { const t = new Date(); const f = new Date(); f.setDate(f.getDate()-6); return [_iso(f), _iso(t)]; }
    if (dateChip === '30d')   { const t = new Date(); const f = new Date(); f.setDate(f.getDate()-29); return [_iso(f), _iso(t)]; }
    if (dateChip === 'custom') return [dateFrom || '', dateTo || ''];
    return ['', ''];
  };
  // STEP 3-OPS-B · Carrier-agnostic Excel workflow state
  const [bulkInvoiceOpen, setBulkInvoiceOpen] = useState(false);
  // STEP 3-WMS-B PHASE 2-C-FRONTEND-HOTFIX · Combined Shipment (직접배송 합포)
  //   Wiring to SimpleOrders (actual seller entry point). Reuses existing
  //   SellerCombinedShipmentModal + backend /seller/combined-shipments/* APIs.
  //   Do NOT auto-combine · seller must click [📦 합포 가능 N건] to open modal.
  const [combinedOpen, setCombinedOpen] = useState(false);
  const [combinedCount, setCombinedCount] = useState(0);
  // STEP 3-SHIP-B · Carrier selection modal (택배사 양식 선택)
  const [carrierOpen, setCarrierOpen] = useState(false);
  const [carrierRows, setCarrierRows] = useState([]);
  // STEP OMS-UI-3A · 상황별 카톡 안내 modal · Foundation only (no send).
  const [kakaoGuideOpen, setKakaoGuideOpen] = useState(false);
  // STEP OMS-PARTIAL-PICK-1 · 다상품 주문 선택 피킹 modal 대상 (단일 order 단위).
  const [selectivePickTarget, setSelectivePickTarget] = useState(null);
  // { seller_order_id, order_number }
  // STEP 3-WMS-B PHASE 2-D · 3PL Combined Shipment Request
  const [threePLOpen, setThreePLOpen] = useState(false);
  const [threePLCount, setThreePLCount] = useState(0);
  const [threePLRequestCount, setThreePLRequestCount] = useState(0);
  const loadCombined = useCallback(async () => {
    try {
      const [d, t, req] = await Promise.all([
        api.get('/seller/combined-shipments/candidates'),
        api.get('/seller/3pl-combined/candidates').catch(() => ({ data: { items: [] } })),
        api.get('/seller/3pl-combined/requests?status=REQUESTED').catch(() => ({ data: { items: [] } })),
      ]);
      setCombinedCount((d.data?.items || []).length);
      setThreePLCount((t.data?.items || []).length);
      setThreePLRequestCount((req.data?.items || []).length);
    } catch { /* silent · optional hint */ }
  }, []);

  // STEP 3-SHIP-B · Open carrier-selection modal instead of directly writing
  //   the file. The modal loads combined docs, applies PHASE 4 dedup, and
  //   invokes the chosen CARRIER_PROFILES mapper (CJ/HANJIN/LOTTE/STANDARD_LIVEMOA).
  const exportInvoiceXlsx = () => {
    const src = selected.size > 0
      ? rows.filter((r) => selected.has(r.seller_order_id))
      : rows;
    if (src.length === 0) { toast.error('내보낼 주문이 없습니다.'); return; }
    setCarrierRows(src);
    setCarrierOpen(true);
  };

  const stage = STAGES.find((s) => s.key === stageKey);

  const loadCounts = useCallback(async () => {
    try {
      // D-6E FINAL CLEANUP §13 · counts 는 omsStages.fetchOmsCounts 로 통일.
      const [canon, rOps] = await Promise.all([
        fetchOmsCounts(api),
        api.get('/seller/dashboard/ops-summary').catch(() => ({ data: {} })),
      ]);
      setCounts(canon);
      const todos = rOps.data?.todos || {};
      const oms = canon.oms || {};
      setProblemCounts({
        shortage: (oms.waiting_stock || 0) + (oms.has_out_of_stock || 0),
        partial:  oms.partial_shippable || 0,
        inbound:  todos.inbound_waiting?.count || 0,
        rma:      todos.rma_pending?.count || 0,
      });
    } catch { /* ignore */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      // Inline date range (avoid stale closure · aligns with useCallback deps).
      const _computeRange = () => {
        if (dateChip === 'today') { const t = new Date(); return [_iso(t), _iso(t)]; }
        if (dateChip === '7d')    { const t = new Date(); const f = new Date(); f.setDate(f.getDate()-6); return [_iso(f), _iso(t)]; }
        if (dateChip === '30d')   { const t = new Date(); const f = new Date(); f.setDate(f.getDate()-29); return [_iso(f), _iso(t)]; }
        if (dateChip === 'custom') return [dateFrom || '', dateTo || ''];
        return ['', ''];
      };
      const [df, dt] = _computeRange();
      // Sort mapping · legacy: recent|oldest · OMS: created_desc|created_asc.
      const legacySort = sortUi === 'oldest' ? 'oldest' : 'recent';
      const omsSort    = sortUi === 'oldest' ? 'created_asc' : 'created_desc';
      const common = {
        page, limit: PAGE_SIZE,
        ...(searchQ ? { q: searchQ } : {}),
        ...(df ? { date_from: df } : {}),
        ...(dt ? { date_to: dt }   : {}),
      };
      if (stage.mode === 'legacy') {
        // Legacy /seller/orders with status filter · server-side pagination.
        const r = await api.get('/seller/orders', { params: {
          status: stage.legacyStatus, sort: legacySort, ...common,
        } });
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setRows(items);
        setPageInfo({
          total: (r.data && typeof r.data.total === 'number') ? r.data.total : items.length,
          total_pages: (r.data && typeof r.data.page_count === 'number') ? r.data.page_count : 1,
        });
      } else if (stage.key === 'invoice') {
        // SHIPPING V1 FINAL UI CLOSE · ⑥ = awaiting_tracking + inspected 통합 표시.
        //   두 work_status 를 각각 서버 페이지네이션 조회 후 merge.  order.status
        //   ='shipping'/'delivered' 는 ⑦/⑧ 소속이므로 클라이언트 방어 필터.
        const [r1, r2] = await Promise.all([
          api.get('/seller/oms/orders', { params: { work_status: 'awaiting_tracking', sort: omsSort, ...common } }),
          api.get('/seller/oms/orders', { params: { work_status: 'inspected',        sort: omsSort, ...common } }),
        ]);
        const merged = [...(r1.data?.items || []), ...(r2.data?.items || [])]
          .filter((o) => o.status !== 'shipping' && o.status !== 'delivered' && o.status !== 'shipped');
        setRows(merged);
        const t1 = r1.data?.total || 0, t2 = r2.data?.total || 0;
        setPageInfo({
          total: t1 + t2,
          total_pages: Math.max(r1.data?.total_pages || 1, r2.data?.total_pages || 1),
        });
      } else if (stage.key === 'stock_check') {
        // STEP OMS-INVENTORY-CHECK-1 · ③ 재고확인 = 3개 WORK_STATUS 합집합.
        //   stageCount() 는 3개 SUM 하지만 loadRows 는 filterKey 하나만 조회하던
        //   기존 로직으로 인해 count > list 불일치 발생 (예 · count=2, list=1).
        //   합포/다상품 주문의 입고대기 item 이 partial_shippable/has_out_of_stock
        //   버킷에 있어 waiting_stock 만 조회 시 누락되는 원인 · 3-way merge 로
        //   최소 수정 (loadCounts / oms_workflow classify 무변경 · 다른 STEP LOCK).
        const [rA, rB, rC] = await Promise.all([
          api.get('/seller/oms/orders', { params: { work_status: 'waiting_stock',     sort: omsSort, ...common } }),
          api.get('/seller/oms/orders', { params: { work_status: 'partial_shippable', sort: omsSort, ...common } }),
          api.get('/seller/oms/orders', { params: { work_status: 'has_out_of_stock',  sort: omsSort, ...common } }),
        ]);
        // Dedup by seller_order_id · WORK_STATUS 단일값 계약이라 원칙적으로 중복 없지만
        // 방어적으로 seller_order_id 기준 map.
        const _byId = new Map();
        for (const arr of [rA.data?.items || [], rB.data?.items || [], rC.data?.items || []]) {
          for (const o of arr) {
            const _k = o.seller_order_id || o.id;
            if (_k && !_byId.has(_k)) _byId.set(_k, o);
          }
        }
        setRows(Array.from(_byId.values()));
        const _t = (rA.data?.total || 0) + (rB.data?.total || 0) + (rC.data?.total || 0);
        setPageInfo({
          total: _t,
          total_pages: Math.max(rA.data?.total_pages || 1, rB.data?.total_pages || 1, rC.data?.total_pages || 1),
        });
      } else {
        // OMS mode · server-side pagination.
        const wsFilter = stage.filterKey || (Array.isArray(stage.ws) ? stage.ws[0] : stage.ws);
        const params = { work_status: wsFilter, sort: omsSort, ...common };
        const r = await api.get('/seller/oms/orders', { params });
        setRows(r.data?.items || []);
        setPageInfo({
          total: r.data?.total || 0,
          total_pages: r.data?.total_pages || 1,
        });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  }, [stage, page, searchQ, dateChip, dateFrom, dateTo, sortUi]);
  // A4-2A-1 · 필터/스테이지 변경 시 page=1 (loadRows deps 내부에서 무한루프 방지).
  const _resetPageRef = useRef(true);
  useEffect(() => {
    if (_resetPageRef.current) { _resetPageRef.current = false; return; }
    setPage(1);
  }, [stageKey, searchQ, dateChip, dateFrom, dateTo, sortUi]);
  // Debounced search input → searchQ (500ms).
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchQ(searchInput.trim()), 500);
    return () => searchTimerRef.current && clearTimeout(searchTimerRef.current);
  }, [searchInput]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadCombined(); }, [loadCombined]);

  // D-6E FINAL CLEANUP §13 · stageCount 는 omsStages.stageCount REUSE.
  const stageCountFor = (s) => stageCount(counts, s);

  const toggleOne = (sid) => {
    const s = new Set(selected);
    if (s.has(sid)) s.delete(sid); else s.add(sid);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else {
      // Exclude combined-shipment rows on invoice/ship stages · they are locked.
      const combinedLock = (stageKey === 'invoice' || stageKey === 'ship');
      const eligible = combinedLock ? rows.filter((r) => !r.combined_shipment_id) : rows;
      setSelected(new Set(eligible.map((r) => r.seller_order_id)));
    }
  };

  // 단계별 벌크 액션 정의
  const bulkAction = async () => {
    if (selected.size === 0) { toast.error('처리할 주문을 선택해주세요.'); return; }
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      if (stageKey === 'order_confirm') {
        // payment_pending → paid
        const { data } = await api.post('/seller/orders/bulk-status', { seller_order_ids: ids, target_status: 'paid', reason: '간편처리 결제완료' });
        toast.success(`결제완료 ${(data.success_ids || []).length}건 처리 · 실패 ${data.skipped?.length || 0}건`);
      } else if (stageKey === 'payment_confirm') {
        // paid → preparing (bulk-status)
        const { data } = await api.post('/seller/orders/bulk-status', { seller_order_ids: ids, target_status: 'preparing', reason: '간편처리 준비시작' });
        toast.success(`상품준비 ${(data.success_ids || []).length}건 시작 · 실패 ${data.skipped?.length || 0}건`);
      } else if (stageKey === 'stock_check') {
        // Navigate to inventory instead of bulk action
        goTo('inventory');
        setBulkBusy(false); return;
      } else if (stageKey === 'invoice') {
        // SHIPPING V1 FINAL UI CLOSE (2026-02) · ⑥ 발송처리
        //   선택된 [출고대기](tracking 있음) 주문만 order.status='shipping' 으로 전환.
        //   기존 백엔드 /seller/orders/bulk-status target_status='shipping' 재사용 (신규 API 없음).
        const selectedSet = new Set(ids);
        const shipIds = rows
          .filter((s) => selectedSet.has(s.seller_order_id)
                          && s.shipment?.tracking_number
                          && isShipEligible(s))
          .map((s) => s.seller_order_id);
        if (!shipIds.length) {
          toast.error('발송처리 대상이 없습니다. 송장번호가 등록된 [출고대기] 주문만 처리할 수 있습니다.');
          setBulkBusy(false); return;
        }
        const { data } = await api.post('/seller/orders/bulk-status',
          { seller_order_ids: shipIds, target_status: 'shipping', reason: '판매자 [선택 주문 발송처리]' });
        const okN = (data.success_ids || []).length;
        const skN = (data.skipped || []).length;
        if (okN && !skN) {
          toast.success(`발송처리 ${okN}건 완료 · ⑦ 발송[출고완료]로 이동`);
        } else if (okN && skN) {
          const reasons = Array.from(new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))).slice(0, 3);
          toast(`발송처리 ${okN}건 · ${skN}건 제외`, {
            description: reasons.length ? reasons.join(' · ') : '자세한 사유는 상세를 확인해주세요.',
            icon: '📦',
          });
        } else {
          const reasons = Array.from(new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))).slice(0, 3);
          toast.error(reasons.length ? reasons.join(' · ') : '발송처리 실패');
        }
      } else if (stageKey === 'ship') {
        // inspected → shipping (판매자 명시 [배송중 처리] · D-6D-C policy)
        // 이미 shipping / shipped 상태인 주문은 목록에 남아 있어도 대상에서 제외.
        const selectedSet = new Set(ids);
        const shipIds = rows
          .filter((s) => selectedSet.has(s.seller_order_id) && isShipEligible(s))
          .map((s) => s.seller_order_id);
        if (!shipIds.length) {
          toast.error('배송중 처리 대상 주문이 없습니다. (이미 배송중이거나 검수 미완료)');
          setBulkBusy(false); return;
        }
        const { data } = await api.post('/seller/orders/bulk-status',
          { seller_order_ids: shipIds, target_status: 'shipping', reason: '판매자 [배송중 처리]' });
        const okN = (data.success_ids || []).length;
        const skN = (data.skipped || []).length;
        if (okN && !skN) {
          toast.success(`배송중 ${okN}건 처리 완료`);
        } else if (okN && skN) {
          const reasons = Array.from(new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))).slice(0, 3);
          toast(`배송중 ${okN}건 처리 · ${skN}건 제외`, {
            description: reasons.length ? reasons.join(' · ') : '자세한 사유는 상세를 확인해주세요.',
            icon: '📦',
          });
        } else {
          const reasons = Array.from(new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))).slice(0, 3);
          toast.error(reasons.length ? reasons.join(' · ') : '배송중 처리 실패');
        }
      }
      await loadCounts();
      await loadRows();
      await loadCombined();
    } catch (e) { toast.error(e?.response?.data?.detail || '처리 실패'); }
    finally { setBulkBusy(false); }
  };

  const bulkActionLabel = {
    order_confirm:   '결제 완료로 처리',
    payment_confirm: '상품 준비 시작',
    stock_check:     '재고 관리로 이동',
    prepare:         '피킹 시작',
    invoice:         '주문 발송처리',
    ship:            '',
    done:            '',
  }[stageKey];

  const bulkActionIcon = {
    order_confirm:   CheckCircle2,
    payment_confirm: PackagePlus,
    stock_check:     ChevronRight,
    prepare:         Play,
    invoice:         Truck,
    ship:            null,
    done:            null,
  }[stageKey];

  const IconComp = bulkActionIcon;

  // ⑦ 발송 · Frontend eligibility · 이미 shipping / delivered 상태 주문은
  //   [발송처리] 대상에서 제외한다. 백엔드 shipping guard 는 별도 검증이
  //   되어 있으나, UI 카운트/버튼 노출도 함께 정합해야 판매자 혼란을 방지.
  const isShipEligible = (o) => {
    if (!o) return false;
    if (o.status === 'shipping' || o.status === 'delivered') return false;
    const fs = o.fulfillment?.status || 'pending';
    if (fs === 'shipped' || fs === 'delivered') return false;
    return true;
  };
  // SHIPPING V1 FINAL UI CLOSE · ⑥ [선택 주문 발송처리] 대상 = tracking 있는 것만.
  const eligibleInvoiceShipSelectedCount = stageKey === 'invoice'
    ? rows.filter((r) => selected.has(r.seller_order_id)
                          && r.shipment?.tracking_number
                          && isShipEligible(r)).length
    : 0;
  const bulkBtnVisibleCount = stageKey === 'invoice' ? eligibleInvoiceShipSelectedCount : selected.size;

  // SHIPPING V1 FINAL UI CLOSE · 발송처리 버튼 클릭 → 확인 모달 오픈.
  const handleBulkClick = () => {
    if (stageKey === 'invoice') {
      if (eligibleInvoiceShipSelectedCount === 0) {
        toast.error('송장번호가 등록된 [출고대기] 주문을 선택해주세요.');
        return;
      }
      setShipConfirmOpen(true);
      return;
    }
    bulkAction();
  };

  return (
    <div className="space-y-4" data-testid="simple-orders-section">
      <BankDepositsPanel onChanged={() => { loadCounts(); loadRows(); }} />
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">주문</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            주문 처리
          </h2>
          <p className="mt-1 text-xs text-gray-500">단계를 눌러 처리할 주문을 확인하세요.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {combinedCount > 0 && (
            <button onClick={() => setCombinedOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-full"
              data-testid="simple-orders-combined-open"
              title="같은 수령인의 여러 직접배송 주문을 하나의 배송으로 묶습니다.">
              📦 합포 가능 {combinedCount}건
            </button>
          )}
          {(threePLCount > 0 || threePLRequestCount > 0) && (
            <button onClick={() => setThreePLOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-full"
              data-testid="simple-orders-3pl-open"
              title="3PL 배정 주문 합포는 물류센터 확인 후 확정됩니다.">
              📦 3PL 합포 요청 {threePLCount > 0 ? `가능 ${threePLCount}건` : ''}{threePLRequestCount > 0 ? ` · 요청중 ${threePLRequestCount}건` : ''}
            </button>
          )}
          <button onClick={() => { loadCounts(); loadRows(); loadCombined(); }} className="inline-flex items-center gap-1 px-3 py-1.5 border border-black/10 dark:border-white/10 text-xs font-bold rounded-full" data-testid="simple-orders-refresh">
            <RefreshCw size={12} /> 새로고침
          </button>
          <button onClick={onOpenDetailedSection} className="inline-flex items-center gap-1 px-3 py-1.5 border border-black/10 dark:border-white/10 text-xs font-bold rounded-full" data-testid="simple-orders-detail-btn">
            상세 관리 →
          </button>
        </div>
      </div>

      {/* 8 단계 트래커 · STEP OMS-UI-1 · ⑤ 는 페이지 이탈 없이 embed 전환 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="simple-orders-stages">
        {STAGES.map((s) => (
          <button key={s.key} onClick={() => setStageKey(s.key)}
            className={`p-3 rounded-lg border-2 text-left transition ${
              stageKey === s.key
                ? 'border-[#FF3B30] bg-[#FF3B30]/5 ring-2 ring-[#FF3B30]/20'
                : (stageCountFor(s) > 0 ? 'border-[#FF3B30]/40 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/60' : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]')
            }`}
            data-testid={`simple-orders-stage-${s.key}`}>
            <p className={`text-[11px] font-bold ${stageKey === s.key ? 'text-[#FF3B30]' : 'text-gray-500'}`}>{s.label}</p>
            <p className="mt-0.5 text-xl md:text-2xl font-black tabular-nums" data-testid={`simple-orders-stage-count-${s.key}`}>
              {stageCountFor(s).toLocaleString()}<span className="text-xs font-bold text-gray-400 ml-0.5">건</span>
            </p>
          </button>
        ))}
      </div>

      {/* 문제 주문 강조 */}
      <div className="rounded-lg border border-amber-400/40 bg-amber-50/50 dark:bg-amber-500/10 p-3" data-testid="simple-orders-problem-bar">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} className="text-amber-600" />
          <p className="text-xs font-black uppercase tracking-widest text-amber-900 dark:text-amber-200">문제 주문 · 예외 처리 필요</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="simple-orders-problem-chips">
          {PROBLEM_TABS.map((p) => {
            const c = problemCounts[p.key] || 0;
            return (
              <button key={p.key}
                onClick={() => {
                  if (p.key === 'shortage') setStageKey('stock_check');
                  else if (p.key === 'partial') setStageKey('stock_check');
                  else if (p.key === 'rma') onOpenDetailedSection?.('rma');
                  else if (p.key === 'inbound') goTo('inventory', { detail: 'inbound' });
                }}
                disabled={c === 0}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold border ${
                  c > 0 ? 'bg-white dark:bg-[#141414] border-amber-500/50 hover:border-amber-600' : 'opacity-40 border-black/10 dark:border-white/10'
                }`}
                data-testid={`simple-orders-problem-${p.key}`}>
                {p.label} <span className="tabular-nums text-amber-700">{c}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* LIGHTPAY-REFUND-HISTORY-UI-1 · 카드 취소·환불 주문 필터 (환불 주문도 조회 유지) */}
      <details className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3" data-testid="simple-orders-refunds">
        <summary className="text-xs font-black cursor-pointer" data-testid="simple-orders-refunds-toggle">카드 취소·환불 주문</summary>
        <div className="mt-2"><CardTransactionsPanel defaultFilter="any" onOpenOrder={onOpenUnifiedDetail} /></div>
      </details>

      {/* STEP OMS-UI-1 · ⑤ 출고관리 embed · 기존 FulfillmentSection REUSE.
          filter/list/detail-link 자리를 통째로 대체 (상단 ①~⑧ 트래커/문제 주문 바
          는 상단에 그대로 유지).  기존 route 및 컴포넌트/백엔드/스캔 계약 무손상. */}
      {stageKey === 'fulfillment' ? (
        <div data-testid="simple-orders-fulfillment-embed">
          <FulfillmentSection onOpenUnifiedDetail={onOpenUnifiedDetail} />
        </div>
      ) : (
      <>
      {/* A4-2A-1 · 공통 필터/검색/정렬 바 · 서버측 페이지네이션과 연동 */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3 space-y-2"
            data-testid="simple-orders-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="주문번호 · 구매자명 · 전화번호 · 상품명 · SKU · 송장번호"
              className="w-full pl-7 pr-7 py-1.5 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-xs focus:outline-none focus:border-[#FF3B30]"
              data-testid="simple-orders-search-input"
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSearchQ(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#FF3B30]"
                aria-label="검색어 지우기"
                data-testid="simple-orders-search-clear">
                <XIcon size={12} />
              </button>
            )}
          </div>
          <select value={sortUi} onChange={(e) => setSortUi(e.target.value)}
            className="px-2 py-1.5 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-xs"
            data-testid="simple-orders-sort">
            <option value="recent">최신순</option>
            <option value="oldest">오래된순</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="simple-orders-date-chips">
          {[
            { key: 'all',   label: '전체' },
            { key: 'today', label: '오늘' },
            { key: '7d',    label: '7일' },
            { key: '30d',   label: '30일' },
            { key: 'custom', label: '기간 지정' },
          ].map((c) => (
            <button key={c.key} onClick={() => setDateChip(c.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                dateChip === c.key
                  ? 'border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]'
                  : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40'
              }`}
              data-testid={`simple-orders-date-chip-${c.key}`}>
              {c.label}
            </button>
          ))}
          {dateChip === 'custom' && (
            <>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-[11px]"
                data-testid="simple-orders-date-from" />
              <span className="text-[11px] text-gray-400">~</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-[11px]"
                data-testid="simple-orders-date-to" />
            </>
          )}
          {(searchQ || dateChip !== 'all') && (
            <button onClick={() => { setSearchInput(''); setSearchQ(''); setDateChip('all'); setDateFrom(''); setDateTo(''); }}
              className="ml-1 px-2 py-1 rounded-full text-[11px] font-bold text-gray-500 hover:text-[#FF3B30] hover:underline"
              data-testid="simple-orders-filter-clear">
              필터 초기화
            </button>
          )}
        </div>
      </div>

      {/* 리스트 + 다중선택 + 벌크 처리 */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]" data-testid="simple-orders-list-panel">
        <div className="p-3 border-b border-black/5 dark:border-white/5 flex items-center gap-2 flex-wrap">
          <p className="text-sm font-black">{stage.label} · {stage.short}</p>
          <span className="text-xs text-gray-500" data-testid="simple-orders-total-count">
            총 <b className="text-black dark:text-white tabular-nums">{(pageInfo.total || 0).toLocaleString()}</b>건
            {pageInfo.total_pages > 1 && (
              <span className="ml-1 text-gray-400">· 페이지 {page}/{pageInfo.total_pages}</span>
            )}
          </span>
          {/* STEP OMS-UI-2 · 주문 선택 체크박스 공통화 · ①~⑧ 중 embed 대상인 ⑤ 를
              제외한 모든 일반 주문 stage 에서 [전체 선택] + row checkbox 노출.
              bulk 처리 버튼은 실제 bulk action 이 있는 stage 에서만 노출 (done/ship
              은 액션 없음 · stock_check 은 하단 별도 [재고 관리로 이동] 버튼 REUSE). */}
          {rows.length > 0 && (
            <>
              <label className="ml-auto flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} className="accent-[#FF3B30]" data-testid="simple-orders-select-all" />
                전체 선택
              </label>
              {selected.size > 0 && bulkBtnVisibleCount > 0 && bulkActionLabel && stageKey !== 'stock_check' && (
                <button onClick={handleBulkClick} disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF3B30] text-white text-xs font-black rounded-full disabled:opacity-60"
                  data-testid="simple-orders-bulk-btn">
                  {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : (IconComp ? <IconComp size={12} /> : null)}
                  선택 {bulkBtnVisibleCount}건 {bulkActionLabel}
                </button>
              )}
              {/* STEP OMS-UI-3A · 상황별 카톡 안내 (Foundation) · 일반 stage 만 노출.
                  ⑤ 출고관리 (fulfillment · embed) 는 자체 selection 유지 → 미노출. */}
              {selected.size > 0 && stageKey !== 'fulfillment' && (
                <button onClick={() => setKakaoGuideOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs font-black rounded-full"
                  data-testid="simple-orders-kakao-guide-btn"
                  title="입고·배송 지연 / 일부상품 선발송 / 품절·변경 안내">
                  💬 상황별 카톡 안내 · {selected.size}건
                </button>
              )}
            </>
          )}
          {stageKey === 'stock_check' && (
            <button onClick={bulkAction}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF3B30] text-white text-xs font-black rounded-full"
              data-testid="simple-orders-bulk-btn">
              {IconComp && <IconComp size={12} />} {bulkActionLabel}
            </button>
          )}
          {/* A4-2A-1 · 일반 주문관리 Excel 다운로드 · 단계별 정책:
              ① 주문확인 · ⑥ 송장등록 은 미노출 (①=개인정보 최소 · ⑥=택배사 양식이 별도).
              허용 stage: payment_confirm(②) · stock_check(③) · prepare(④) ·
                          fulfillment(⑤ · nav 이지만 안전 유지) · ship(⑦) · done(⑧). */}
          {['payment_confirm', 'stock_check', 'fulfillment', 'ship', 'done'].includes(stageKey) && (
            <button onClick={async () => {
              const ids = selected.size > 0 ? Array.from(selected) : rows.map((r) => r.seller_order_id);
              if (!ids.length) { toast.error('다운로드할 주문이 없습니다.'); return; }
              try {
                const { downloadOrders } = await import('../exportOrders');
                await downloadOrders({ format: 'xlsx', ids });
              } catch { /* toast handled inside downloadOrders */ }
            }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white text-xs font-black rounded-full"
              data-testid="simple-orders-general-export"
              title="주문/수취인/상품/랙 위치 포함 · LIVEMOA 자체 업무용 양식">
              <FileSpreadsheet size={12}/>
              선택 {selected.size > 0 ? selected.size : rows.length}건 주문 엑셀 다운로드
            </button>
          )}
          {/* STEP OMS-OUTBOUND-UNIFY-1 · ④ 상품준비는 ④ 출고관리 embed 로 통합됨 (이 버튼 제거). */}
          {/* STEP 3-OPS-B · ⑤ 송장 등록 stage — Excel 다운로드 / 일괄등록 (Carrier-agnostic fallback) */}
          {stageKey === 'invoice' && (
            <>
              {selected.size > 0 && (
                <button onClick={exportInvoiceXlsx}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-black rounded-full"
                  data-testid="simple-orders-invoice-export">
                  <FileSpreadsheet size={12}/> 선택 {selected.size}건 출고 엑셀 다운로드
                </button>
              )}
              <button onClick={() => setBulkInvoiceOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f] text-xs font-black rounded-full"
                data-testid="simple-orders-invoice-upload">
                <Upload size={12}/> 송장 엑셀 일괄등록
              </button>
              {/* D-6E FINAL CLEANUP §7 · [CJ 결과 가져오기] 버튼 제거.
                  판매자 V1 흐름: CJ 결과 Excel 다운로드 → Agent watcher
                  자동감지 → LIVEMOA 송장 자동등록.  result-download
                  backend/Agent 코드는 유지 (§7 · UI 만 제거). */}
            </>
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center" data-testid="simple-orders-loading">
            <Loader2 className="animate-spin" size={16} /> 불러오는 중…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-xs text-gray-400" data-testid="simple-orders-empty">
            <ShoppingBag className="mx-auto mb-2 text-gray-300" size={22} />
            <p>이 단계에 해당하는 주문이 없습니다.</p>
          </div>
        ) : (
          <div className="divide-y divide-black/5 dark:divide-white/5" data-testid="simple-orders-list">
            {rows.map((o) => {
              const sid = o.seller_order_id;
              const first = (o.items || [])[0] || {};
              const bs = o.buyer_snapshot || {};
              const buyer = bs.name || o.buyer_name || '-';
              const phone = bs.phone || (o.shipping_address_snapshot?.phone) || '';
              const total = (o.totals?.total) || o.total_amount || 0;
              const firstImg = first.image_snapshot || first.image || first.thumbnail || '';
              const firstName = first.product_name_snapshot || first.name_snapshot || first.name || '-';
              const firstOpt = (first.option || first.options);
              const firstOptText = firstOpt && typeof firstOpt === 'object'
                ? Object.entries(firstOpt).filter(([, v]) => v !== null && v !== '').map(([, v]) => v).join(' / ')
                : (typeof firstOpt === 'string' ? firstOpt : '');
              const firstQty = Number(first.quantity || 0) || 0;
              const moreCount = Math.max(0, (o.items || []).length - 1);
              return (
                <div key={sid} className={`px-3 py-2.5 flex items-center gap-3 hover:bg-black/5 dark:hover:bg-white/5 ${selected.has(sid) ? 'bg-[#FF3B30]/5' : ''}`} data-testid={`simple-orders-row-${sid}`}>
                  {/* STEP OMS-UI-2 · 모든 일반 주문 stage 에서 row checkbox 표시.
                      onClick stopPropagation → row 자체 클릭 (주문 상세) 과 분리. */}
                  {o.combined_shipment_id && stageKey === 'invoice'
                    ? <span className="w-4 inline-flex" title="합포배송 · 합포 송장에서 처리하세요"
                              data-testid={`simple-orders-combined-lock-${sid}`}>🔒</span>
                    : <input type="checkbox" checked={selected.has(sid)} onChange={() => toggleOne(sid)} className="accent-[#FF3B30]" data-testid={`simple-orders-check-${sid}`} onClick={(e) => e.stopPropagation()} />
                  }
                  {/* A4-2A-1 · 상품 썸네일 (40x40 compact) */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-md overflow-hidden bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center justify-center"
                        data-testid={`simple-orders-thumb-${sid}`}>
                    {firstImg
                      ? <img src={firstImg} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }} />
                      : <Package size={16} className="text-gray-300" />}
                  </div>
                  <button onClick={() => onOpenUnifiedDetail?.(sid)} className="flex-1 min-w-0 text-left" data-testid={`simple-orders-open-${sid}`}>
                    <p className="text-[11px] font-bold text-gray-500 flex items-center gap-1 flex-wrap">
                      <span data-testid={`simple-orders-order-no-${sid}`}>{o.order_number}</span>
                      <span className="text-gray-300">·</span>
                      <span className="font-black text-gray-700 dark:text-gray-200">{buyer}</span>
                      {phone && <span className="text-gray-400 font-mono">{phone}</span>}
                    </p>
                    <p className="text-sm font-semibold truncate mt-0.5" data-testid={`simple-orders-product-${sid}`}>
                      {firstName}
                      {firstOptText && <span className="text-gray-400 font-normal"> · {firstOptText}</span>}
                      {firstQty > 0 && <span className="text-gray-500 font-semibold"> × {firstQty}</span>}
                      {moreCount > 0 && <span className="text-gray-400"> 외 {moreCount}건</span>}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap items-center gap-1">
                      {o.created_at && <span>{format(new Date(o.created_at), 'M/d HH:mm', { locale: ko })}</span>}
                      {/* SHIPPING V1 FINAL UI CLOSE · ⑥ 송장대기/출고대기 · ⑦ 출고완료 배지 */}
                      {stageKey === 'invoice' && (
                        o.shipment?.tracking_number ? (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold"
                                data-testid={`simple-orders-badge-ready-${sid}`}
                                title="송장번호가 등록되었습니다. [선택 주문 발송처리]를 눌러 출고완료 처리하세요.">
                            [출고대기] {o.shipment.courier_name || o.shipment.courier || '-'} · {o.shipment.tracking_number}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold"
                                data-testid={`simple-orders-badge-await-${sid}`}
                                title="송장번호가 아직 등록되지 않았습니다. 송장 엑셀 일괄등록 또는 상세에서 송장을 입력하세요.">
                            [송장대기]
                          </span>
                        )
                      )}
                      {stageKey === 'ship' && (
                        <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-bold"
                              data-testid={`simple-orders-badge-shipped-${sid}`}
                              title="발송처리가 완료된 주문입니다. (V1은 실시간 배송추적 없음)">
                          [출고완료]
                          {o.shipment?.tracking_number
                            ? ` · ${o.shipment.courier_name || o.shipment.courier || '-'} · ${o.shipment.tracking_number}`
                            : ''}
                        </span>
                      )}
                      {stageKey !== 'invoice' && stageKey !== 'ship' && o.work_status_label && (
                        <span className="px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10">{o.work_status_label}</span>
                      )}
                      {o.combined_shipment_id && (
                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold"
                              data-testid={`simple-orders-combined-badge-${sid}`}
                              title="이 주문은 다른 주문과 함께 하나의 배송으로 처리됩니다.">
                          📦 합포배송
                        </span>
                      )}
                      {o.combined_shipment_id && (stageKey === 'invoice' || stageKey === 'ship') && (
                        <span className="text-[10px] text-amber-700 font-bold"
                              data-testid={`simple-orders-combined-note-${sid}`}>
                          · 합포 송장에서 처리
                        </span>
                      )}
                    </p>
                  </button>
                  <p className="text-sm font-black flex-shrink-0" data-testid={`simple-orders-amount-${sid}`}>{won(total)}</p>
                </div>
              );
            })}
          </div>
        )}
        {/* A4-2A-1 · 서버측 페이지네이션 (50건/페이지) */}
        {pageInfo.total_pages > 1 && !loading && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-black/5 dark:border-white/5"
                data-testid="simple-orders-pagination">
            <p className="text-[11px] text-gray-500 tabular-nums">
              총 <b>{(pageInfo.total || 0).toLocaleString()}</b>건 · 페이지 <b>{page}</b>/{pageInfo.total_pages}
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold disabled:opacity-40"
                data-testid="simple-orders-page-prev">
                <ChevronLeft size={12}/> 이전
              </button>
              <button onClick={() => setPage((p) => Math.min(pageInfo.total_pages, p + 1))} disabled={page >= pageInfo.total_pages}
                className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold disabled:opacity-40"
                data-testid="simple-orders-page-next">
                다음 <ChevronRight size={12}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 하단 상세 관리 */}
      <div className="text-center pt-2">
        <button onClick={onOpenDetailedSection} className="text-xs font-bold text-gray-500 hover:text-[#FF3B30] hover:underline" data-testid="simple-orders-detail-bottom">
          검색·필터·저장된 보기 등 <b>상세 관리 (기존 OMS 주문 작업대)</b> →
        </button>
      </div>
      </>
      )}
      <BulkInvoiceUploadModal
        open={bulkInvoiceOpen}
        onClose={() => setBulkInvoiceOpen(false)}
        onDone={() => { setBulkInvoiceOpen(false); loadCounts(); loadRows(); }}
        onOpenInvoiceTemplate={exportInvoiceXlsx}
      />
      <SellerCombinedShipmentModal
        open={combinedOpen}
        onClose={() => setCombinedOpen(false)}
        onChanged={() => { loadRows(); loadCounts(); loadCombined(); }}
      />
      <Seller3PLCombinedRequestModal
        open={threePLOpen}
        onClose={() => setThreePLOpen(false)}
        onChanged={() => { loadRows(); loadCounts(); loadCombined(); }}
      />
      <CarrierExportModal
        open={carrierOpen}
        onClose={() => setCarrierOpen(false)}
        rows={carrierRows}
      />
      {/* STEP OMS-UI-3A · 상황별 카톡 안내 · Foundation only.  실제 발송 없음. */}
      <KakaoContextGuideModal
        open={kakaoGuideOpen}
        onClose={() => setKakaoGuideOpen(false)}
        sellerOrderIds={selected}
      />
      {/* STEP OMS-PARTIAL-PICK-1 · 다상품 주문 선택 피킹 · 성공 시 ⑤ 출고관리 자동 이동. */}
      <SelectivePickModal
        open={!!selectivePickTarget}
        sellerOrderId={selectivePickTarget?.seller_order_id}
        orderNumber={selectivePickTarget?.order_number}
        onClose={() => setSelectivePickTarget(null)}
        onPicked={() => {
          setSelectivePickTarget(null);
          setSelected(new Set());
          loadCounts(); loadRows();
          setStageKey('fulfillment');
        }}
      />

      {/* SHIPPING V1 FINAL UI CLOSE (2026-02) · ⑥ 발송처리 확인 모달
          · [출고대기] 주문(tracking 있음)만 대상 · order.status → 'shipping'
          · 기존 백엔드 /seller/orders/bulk-status 재사용 · V1은 배송추적 없음. */}
      {shipConfirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
              onClick={() => !bulkBusy && setShipConfirmOpen(false)}
              data-testid="ship-confirm-overlay">
          <div className="bg-white dark:bg-[#141414] rounded-xl p-5 max-w-sm w-full shadow-2xl border border-black/10 dark:border-white/10"
                onClick={(e) => e.stopPropagation()}
                data-testid="ship-confirm-modal">
            <h3 className="text-base font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>발송 처리 확인</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              선택한 <b className="text-[#FF3B30]">{eligibleInvoiceShipSelectedCount}건</b>의 [출고대기] 주문을
              <br/>⑦ 발송 <b>[출고완료]</b> 상태로 전환합니다.
            </p>
            <p className="mt-2 text-[11px] text-gray-500">
              · 실제 택배 인계 후 클릭하세요.<br/>
              · 발송처리 이후에는 되돌리기가 어렵습니다.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShipConfirmOpen(false)} disabled={bulkBusy}
                className="px-3 py-2 border border-black/10 dark:border-white/10 text-xs font-bold rounded-full disabled:opacity-60"
                data-testid="ship-confirm-cancel">
                취소
              </button>
              <button onClick={() => { setShipConfirmOpen(false); bulkAction(); }} disabled={bulkBusy}
                className="px-4 py-2 bg-[#FF3B30] text-white text-xs font-black rounded-full disabled:opacity-60 inline-flex items-center gap-1"
                data-testid="ship-confirm-ok">
                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Truck size={12} />}
                확인 · 발송처리
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
